import {runIfExist} from '@convincely/utils/lib/common.js';
import {dateTimeFormatFn} from '@convincely/utils/lib/date.js';
import {defined} from '@convincely/utils/lib/defined.js';
import {get} from '@convincely/utils/lib/object.js';
import {empty, space} from '@convincely/utils/lib/strings.js';
import {default as chunk} from 'lodash-es/chunk.js';
import {stripNewlines} from '../../utils/clean.js';
import {isOption} from '../../utils/options.js';
import {dataStore} from '../Customiser/store.js';
import {generatePhoneLink} from '../Telephone/utils.js';
import {getRawType, number, object, string} from '@convincely/utils/lib/types.js';
import {clone} from '@convincely/utils/lib/clone.js';
import {currency} from '../../currency.js';
import {capitalizeFirstLetter} from '../../utils/strings.js';

const braces = /\{([a-z.0-9_|:/]+)\}/gi;
const sepContext = /\s*---(.*?)---\s*/g;
const listSep = ', ';
const indexSeparatorRegex = /(?=\[)/;
const mapperDefault = {};
const _data = {};

export function formatValueWithLocale(value, locale, config = {}) {
  return locale && typeof value === 'number' ? value.toLocaleString(locale, config) : value;
}

/**
 * @param {Store} inputs
 * @param {string} content
 * @param {object} inputMap
 * @param {object} [map]
 * @param {string} [fallback]
 * @param {Function} [callback]
 * @param {object} [data]
 * @param {string} [locale]
 * @param {object} [localeConfig]
 * @return {string}
 */
export function getContent(inputs, content, inputMap = {}, map = mapperDefault, fallback, callback = stripNewlines, data = _data, locale = undefined, localeConfig = {}) {
  const str = String(content);
  const sections = String(str).split(sepContext);

  if (sections.length > 1) {
    let [text, ...options] = sections;

    for (const [logic, value] of chunk(options, 2)) {
      if (isOption(inputs, logic)) {
        text = value;
        break;
      }
    }

    return getContent(inputs, text, inputMap, map, fallback, callback, data, locale, localeConfig);
  }

  return str.replace(braces, (m, nameStr) => {
    const [nameLine, propLine = '', ...others] = nameStr.split('.');
    const [name, fnLine1 = ''] = nameLine.split('|');
    const [prop, fnLine2 = ''] = propLine.split('|');
    const [fn, ...params] = (fnLine1 || fnLine2).split(':');
    const mapValue = map?.[name] || data?.[name] || (defined(inputs[name]) && inputs[name][prop]);
    const param = params.join(':') + (others.length > 0 ? `.${others.join('.')}` : empty);
    const resultParams = [inputs, `{${param}}`, inputMap, map, fallback, callback, data];

    if (defined(mapValue)) {
      return resolveResult(resolve(mapValue[prop] || mapValue || fallback, fn, param, map, params), resultParams);
    }

    const value = formatValueWithLocale(inputs[name], locale, localeConfig);
    const items = inputMap[name];
    const combinedMap = {...inputMap, ...map};

    if (items && items.list?.length > 0) {
      runIfExist(items.callback, inputs, items, items.singular);
      const itemArr = getItem(name, value, items);
      const label = itemArr.map((item) => item && callback(item[prop] === empty ? empty : item[prop] || item.display || item[items.label]));

      if (label.length < 1) {
        return fallback || empty;
      }

      return resolveResult(resolve(label || fallback, fn, param, combinedMap, params), resultParams) || empty;
    }

    const result = resolveResult(resolve(defined(value) ? value : value || fallback, fn, param, combinedMap, params), resultParams);

    return defined(result) ? result : empty;
  });
}

export const getContentFromMap = (content, map, fallback, callback) => getContent({}, content, {}, map, fallback, callback);

export const getContentWithIndex = (inputs, content, ...args) => {
  try {
    const [contentStr, index] = content.split(indexSeparatorRegex);

    const resolvedValue = getContent(inputs, contentStr, ...args);
    const valueArray = resolvedValue.split(listSep);
    const nestedValue = get(valueArray, index);

    return index ? nestedValue : resolvedValue;
  } catch (e) {
    console.error('Not possible to get content index', e);
  }
};

/**
 * @param {string} name
 * @param {*} value
 * @param {DataItems} items
 * @return {object}
 */
export function getItem(name, value, items) {
  if (items) {
    return defined(value) ? (Array.isArray(value) ? value : [value]).map((v) => items.getByKey(v)) : [];
  }

  return value;
}

const fnResolvers = {
  fallback(content) {
    return typeof content === 'object' ? getContent : content;
  },
  date(content, param) {
    return dateTimeFormatFn(content, param);
  },
  last(content, param) {
    return String(content).substr(-param || 0);
  },
  map(content, param, map) {
    return map[content] || map[param];
  },
  plural(content, param, map, [single, plural]) {
    return content + space + (Number(content) === 1 ? single : plural);
  },
  lowerCase(content) {
    return String(content).toLowerCase();
  },
  list(content, param, map, [separator = '&', ifEmpty = '']) {
    const last = content.pop();

    return (content.length > 0 ? `${content.join(',\n')} ${separator}\n${last}` : last) || ifEmpty;
  },
  listNewLine(content) {
    return content && content.length > 0 ? content.join('\n').trim() : '';
  },
  listTab(content, param) {
    const last = content.pop();

    return (content.length > 0 ? `\t${content.join('\n\t')}\n\t${last}` : `\t${last}`) || param;
  },
  listCap(content, cap = 1) {
    return Array.isArray(content) && content.length > 1 ? content.slice(0, cap).concat(`\n+${content.length - Number(cap)} more`).join(', ') : String(content);
  },
  listNewLinesCap(content, cap = 1) {
    let cappedString = Array.isArray(content) && content.length > 1 ? content.slice(0, cap).join('\n') : String(content);

    if (content.length > Number(cap)) {
      cappedString = cappedString.concat('\n', `+${content.length - Number(cap)} more`);
    }

    return cappedString;
  },
  phoneLink(content) {
    return generatePhoneLink(content);
  },
  usPhoneFormat(content) {
    return content ? content.replace(/[()]/g, '').trim().replace(/\s+/g, '-') : '';
  },
  dollarFormatNoZero(content) {
    return currency(content, 0, true);
  },
  dollarFormat(content) {
    return currency(content);
  },
  percentage(content) {
    return typeof content === 'number' ? `${content.toFixed(2)}%` : `${content}%`;
  },
  capitalize(content = '') {
    return capitalizeFirstLetter(content);
  },
  ordinal(content) {
    const rules = new Intl.PluralRules('en-US', {type: 'ordinal'});
    const suffixes = new Map([
      ['one', 'st'],
      ['two', 'nd'],
      ['few', 'rd'],
      ['other', 'th'],
    ]);
    const rule = rules.select(content);

    return `${content}${suffixes.get(rule)}`;
  },
};

function resolveResult(result, resultParams) {
  return typeof result === 'function' ? result(...resultParams) : result;
}

function resolve(str, fn, ...args) {
  const content = Array.isArray(str) ? fn ? str : str.join(listSep) : str;

  return fnResolvers[fn] ? fnResolvers[fn](content, ...args) : content;
}

dataStore.subscribe((value) => {
  Object.assign(_data, value);
});

export function getContentIntoComponentsProps(components = [], data = {}) {
  return components.map((current) => {
    const {props, ...component} = clone(current);
    let target;

    if (props) {
      component.props = {...props};
      target = component.props;
    } else {
      target = component;
    }

    Object.keys(target).forEach((key) => {
      if ([string, number].includes(getRawType(target[key]))) {
        component[key] = getContentFromMap(target[key], data);
      } else if (getRawType(target[key]) === object) {
        const nestedProps = target[key];

        Object.keys(nestedProps)
          .forEach((propKey) => nestedProps[propKey] = getContentFromMap(nestedProps[propKey], data));
      }
    });

    return component;
  });
}

export function getContentRecursive(target = {}, data) {
  try {
    Object.keys(target).forEach((property) => {
      const targetValue = target[property];

      if ([string, number].includes(getRawType(targetValue))) {
        target[property] = getContentFromMap(targetValue, data);
      } else if (Array.isArray(targetValue)) {
        target[property] = targetValue.map((item) => getContentRecursive(item, data));
      } else if (getRawType(targetValue) === object) {
        target[property] = getContentRecursive(targetValue, data);
      }
    });

    return target;
  } catch (e) {
    return target;
  }
}
